import React, { useCallback, useEffect, useRef, useState, FC } from 'react';
import Slider from 'react-slick';
import HeroSectionCarouselArrow from './components/carouselArrow';
import CarouselTransparentNavSection from '../carouselTransparentNavSection';
import { CarouselNavPositionEnum } from '../../../enums/carousel.enums';
import { HeroSectionCarouselModel } from './models/heroSectionCarousel.model';
import { TGalleryItem, TProductHeroSectionProps } from './types/heroSectionCarousel.types';
import ModalDialog from '../modalDialog';
import OverlayCarouselPopup from '../overlayCarouselPopup';
import useMediaQuery from '../../../hooks/useMediaQuery';
import breakpoints from '../../../constants/breakpoints.constants';
import { getYoutubeThumbNail, getYoutubeVideoId } from '../../../utils/youtube.utils';
import HeroCard from './components/heroCard';
import CustomCarouselDots from '../customCarouselDots';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-react';
import Icon from '../icon';
import './styles.scss';

const HeroSectionCarousel: FC<TProductHeroSectionProps> = (props) => {
  const model = new HeroSectionCarouselModel(props);
  const isEEActive = isEditorActive();
  const [currentSlide, _setCurrentSlide] = useState(0);
  const hasMultipleSlides = model.slides?.length > 1;
  const [showOverlay, setShowOverlay] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoPaused, setVideoPaused] = useState(false);
  const [localSlides, setLocalSlides] = useState<TGalleryItem[]>([]);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const sliderRef = useRef<Slider | null>(null);

  const cardsCount = localSlides.length;

  useEffect(() => {
    if (model.slides) {
      let newSlides = [...model.slides];
      newSlides = newSlides.map((slideData) => {
        if (slideData.role === 'extvideo' || slideData.role === 'video') {
          slideData.isVideo = true;
          slideData.videoUrl = slideData.full;
        }

        if (slideData.role === 'extvideo') {
          const videoId = getYoutubeVideoId(slideData.full || '');
          const thumbnailUrl = getYoutubeThumbNail(videoId);
          slideData['rendition_desktop_size1200'] = { url: thumbnailUrl };
        }
        return slideData;
      });
      setLocalSlides(newSlides);
    }
  }, [model.slides]); // eslint-disable-line

  useEffect(() => {
    setVideoPlaying(false);
  }, [currentSlide]);

  const setCurrentSlide = (index: number): void => {
    if (hasMultipleSlides) {
      if (currentSlide !== index) {
        _setCurrentSlide(index);
      }
    }
  };

  const goToPreviousSlide = (): void => {
    const prevSlideNo = currentSlide === 0 ? localSlides.length - 1 : currentSlide - 1;
    sliderRef.current?.slickGoTo(prevSlideNo);
    setVideoPlaying(false);
    setVideoPaused(false);
  };

  const goToNextSlide = (): void => {
    const nextSlideNo = currentSlide === localSlides.length - 1 ? 0 : currentSlide + 1;
    sliderRef.current?.slickGoTo(nextSlideNo);
    setVideoPlaying(false);
    setVideoPaused(false);
  };
  const openOverlayModal = (): void => setShowOverlay(true);
  const closeOverlayModal = (): void => setShowOverlay(false);
  const playCallback = useCallback(() => {
    setVideoPlaying(true);
    setVideoPaused(false);
  }, [setVideoPlaying, setVideoPaused]);
  const pauseCallback = useCallback(() => {
    setVideoPlaying(false);
    setVideoPaused(true);
  }, [setVideoPlaying, setVideoPaused]);
  const endCallback = useCallback(() => {
    setVideoPlaying(false);
    setVideoPaused(false);
  }, [setVideoPlaying, setVideoPaused]);

  const getOverlayProps = useCallback(() => {
    return localSlides.map((slideData) => ({
      ...slideData,
      productImageProps: {
        desktopRendition: slideData['rendition_desktop_size1200'],
        mobileRendition: slideData['rendition_mobile_size500'],
        alt: slideData.alt,
      },
      title: { value: slideData?.title },
      description: { value: slideData?.description },
    }));
  }, [localSlides]);

  const settings = {
    infinite: !isEEActive,
    dots: false,
    arrows: false,
    variableWidth: false,
    speed: 500,
    slidesToScroll: 1,
    afterChange: (index): void => {
      setCurrentSlide(index);
    },
  };

  return (
    <div className="shared-hero-section-carousel">
      <div className="shared-hero-section-carousel__wrapper">
        <Slider {...settings} ref={sliderRef}>
          {localSlides?.map((slide, index) => {
            return (
              /* eslint-disable-next-line */
              <HeroCard
                slide={slide}
                playCallback={playCallback}
                pauseCallback={pauseCallback}
                endCallback={endCallback}
                activeCard={index === currentSlide}
                fromCarousel={true}
              />
            );
          })}
        </Slider>
        {!isDesktop && model.gallery && (
          <div
            className={`${
              'shared-hero-section-carousel__animate-icons shared-hero-section-carousel__animate-icons' +
              (videoPlaying ? '--hide' : '--show')
            } shared-hero-section-carousel__expand-icon `}
            onClick={openOverlayModal}
          >
            <Icon iconName="action-expand" color="primary" size={24} />
          </div>
        )}
        {hasMultipleSlides && (
          <>
            <HeroSectionCarouselArrow
              direction={'previous'}
              onClick={goToPreviousSlide}
              customClass={`
                shared-hero-section-carousel__animate-icons
                ${videoPlaying && 'shared-hero-section-carousel__animate-icons--animate-fade'}
                ${videoPaused && 'shared-hero-section-carousel__animate-icons--show'}
              `}
            />
            <CarouselTransparentNavSection position={CarouselNavPositionEnum.LEFT} handleTouchStart={goToPreviousSlide} />
            <ul
              className={`shared-hero-section-carousel__controls ${
                (videoPlaying || videoPaused) && 'shared-hero-section-carousel__animate-icons--hide'
              }`}
            >
              <CustomCarouselDots cardsCount={cardsCount} activeCard={currentSlide + 1} isDarkMode={true} />
            </ul>
            <CarouselTransparentNavSection position={CarouselNavPositionEnum.RIGHT} handleTouchStart={goToNextSlide} />
            <HeroSectionCarouselArrow
              direction={'next'}
              onClick={goToNextSlide}
              customClass={`
                shared-hero-section-carousel__animate-icons
                ${videoPlaying && 'shared-hero-section-carousel__animate-icons--animate-fade'}
                ${videoPaused && 'shared-hero-section-carousel__animate-icons--show'}
              `}
            />
          </>
        )}
        {!isDesktop && model.gallery && localSlides?.length > 0 && (
          <ModalDialog isOpen={showOverlay} close={closeOverlayModal}>
            <OverlayCarouselPopup slides={getOverlayProps()} showNavDeck={true} showOverlayTitleAndDescription={true} />
          </ModalDialog>
        )}
      </div>
    </div>
  );
};

export default HeroSectionCarousel;
